/** Shared 2D dice-tray payload + local formula eval (board + Atlas plugin). */

export type PathADiceFace = {
  label: string;
  value: number;
  sides: number;
};

export type DiceRollPayload = {
  id: string;
  tokenId?: string;
  tokenName: string;
  abilityName: string;
  formula: string;
  faces: PathADiceFace[];
  modifier: number;
  total: number;
  higher?: string;
  extraModLabel?: string | null;
  /** Board asked Atlas to roll; faces/total are empty until Atlas replies. */
  pending?: boolean;
  source: "board" | "atlas";
  at: number;
};

export type CreatureExperience = {
  name: string;
  modifier: number;
  text: string;
};

export function newDiceRollId(): string {
  return `roll_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

export function evaluateDiceFormula(
  formula: string,
  fallbackModifier = 0,
  baseDieSides = 20,
): { faces: PathADiceFace[]; modifier: number; total: number } {
  const compact = formula.replace(/\s+/g, "");
  const diceRe = /(\d+)?d(\d+)/gi;
  const found: Array<{ n: number; s: number; index: number; len: number }> = [];
  let m: RegExpExecArray | null;
  while ((m = diceRe.exec(compact)) !== null) {
    found.push({
      n: Math.max(1, parseInt(m[1] || "1", 10)),
      s: Math.max(2, parseInt(m[2] || String(baseDieSides), 10)),
      index: m.index,
      len: m[0].length,
    });
  }
  let remainder = compact;
  for (let i = found.length - 1; i >= 0; i--) {
    const d = found[i]!;
    remainder = remainder.slice(0, d.index) + remainder.slice(d.index + d.len);
  }
  let modifier = 0;
  const modRe = /([+-]\d+)/g;
  let mm: RegExpExecArray | null;
  while ((mm = modRe.exec(remainder)) !== null) {
    modifier += parseInt(mm[1]!, 10);
  }
  const faces: PathADiceFace[] = [];
  for (const d of found) {
    for (let i = 0; i < Math.min(d.n, 12); i++) {
      faces.push({
        label: `d${d.s}`,
        value: Math.floor(Math.random() * d.s) + 1,
        sides: d.s,
      });
    }
  }
  if (faces.length === 0) {
    if (/^\d+$/.test(compact)) {
      const n = parseInt(compact, 10);
      return { faces: [], modifier: n, total: n };
    }
    faces.push({
      label: `d${baseDieSides}`,
      value: Math.floor(Math.random() * baseDieSides) + 1,
      sides: baseDieSides,
    });
    if (modifier === 0) modifier = fallbackModifier;
  }
  const total = faces.reduce((sum, f) => sum + f.value, 0) + modifier;
  return { faces, modifier, total };
}

export function extractCreatureExperiences(
  creature: Record<string, unknown>,
): CreatureExperience[] {
  const list: CreatureExperience[] = [];
  const raw = creature.experience ?? creature.experiences;
  if (!raw) return list;

  if (typeof raw === "string") {
    const parts = raw.split(/[,;\n]+/).map((s) => s.trim()).filter(Boolean);
    for (const part of parts) {
      const m = /([+-]?\d+)/.exec(part);
      const mod = m ? parseInt(m[1]!, 10) : 0;
      const name = part.replace(/[+-]?\d+/, "").trim() || "Experience";
      list.push({ name, modifier: mod, text: `${name} ${mod >= 0 ? `+${mod}` : mod}` });
    }
  } else if (Array.isArray(raw)) {
    for (const item of raw) {
      if (typeof item === "string") {
        const m = /([+-]?\d+)/.exec(item);
        const mod = m ? parseInt(m[1]!, 10) : 0;
        const name = item.replace(/[+-]?\d+/, "").trim() || "Experience";
        list.push({ name, modifier: mod, text: `${name} ${mod >= 0 ? `+${mod}` : mod}` });
      } else if (item && typeof item === "object") {
        const rec = item as Record<string, unknown>;
        const name = String(rec.name || rec.title || "Experience").trim();
        const mod =
          typeof rec.modifier === "number"
            ? rec.modifier
            : typeof rec.bonus === "number"
              ? rec.bonus
              : typeof rec.value === "number"
                ? rec.value
                : 0;
        list.push({ name, modifier: mod, text: `${name} ${mod >= 0 ? `+${mod}` : mod}` });
      }
    }
  }
  return list;
}

const STATBLOCK_FENCE_RE =
  /^[ \t]*(?:```+|~~~+)\s*statblock\s*$([\s\S]*?)^[ \t]*(?:```+|~~~+)\s*$/m;
const FRONTMATTER_RE = /^---\r?\n([\s\S]*?)\r?\n---(?:\r?\n|$)/;

function parseSimpleYamlMap(text: string): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const rawLine of text.split(/\r?\n/)) {
    const line = rawLine.trim();
    if (!line || line.startsWith("#")) continue;
    const colon = line.indexOf(":");
    if (colon <= 0) continue;
    const key = line.slice(0, colon).trim();
    let value = line.slice(colon + 1).trim();
    if (!key) continue;
    if (
      (value.startsWith('"') && value.endsWith('"')) ||
      (value.startsWith("'") && value.endsWith("'"))
    ) {
      value = value.slice(1, -1);
    }
    if (value === "true") out[key] = true;
    else if (value === "false") out[key] = false;
    else if (value === "null" || value === "") out[key] = value === "null" ? null : "";
    else if (/^-?\d+(\.\d+)?$/.test(value)) out[key] = Number(value);
    else out[key] = value;
  }
  return out;
}

export function parseCreatureFromNoteText(
  text: string,
): Record<string, unknown> | null {
  if (!text || !text.trim()) return null;
  let creature: Record<string, unknown> = {};
  const fm = FRONTMATTER_RE.exec(text);
  if (fm?.[1]) {
    creature = { ...parseSimpleYamlMap(fm[1]) };
  }
  const fence = STATBLOCK_FENCE_RE.exec(text);
  if (fence?.[1]) {
    creature = { ...creature, ...parseSimpleYamlMap(fence[1]) };
  }
  if (!creature.name && !creature.layout && creature.hp == null && creature.agility == null) {
    return Object.keys(creature).length ? creature : null;
  }
  return creature;
}

function layoutLooksLikePc(creature: Record<string, unknown>): boolean {
  const layout = String(creature.layout || "").toLowerCase();
  return (
    layout.includes("pc") ||
    layout.includes("character") ||
    layout.includes("player")
  );
}

export function isDaggerheartAdversary(creature: Record<string, unknown>): boolean {
  if (layoutLooksLikePc(creature)) return false;
  const layout = String(creature.layout || "").toLowerCase();
  if (layout.includes("adversary") || layout.includes("minion") || layout.includes("monster")) {
    return true;
  }
  const type = String(creature.type || "").toLowerCase();
  if (
    type.includes("minion") ||
    type.includes("adversary") ||
    type.includes("solo") ||
    type.includes("leader")
  ) {
    return true;
  }
  if (creature.difficulty != null) return true;
  if (creature.thresholds != null && creature.hope == null && creature.agility == null) {
    return true;
  }
  return false;
}

export function isDaggerheartPC(creature: Record<string, unknown>): boolean {
  if (layoutLooksLikePc(creature)) return true;
  if (isDaggerheartAdversary(creature)) return false;
  if (creature.hope != null || creature.max_hope != null || creature.hope_max != null) return true;
  if (creature.agility != null || creature.knowledge != null) return true;
  return false;
}

export function parseDiceRollPayload(value: unknown): DiceRollPayload | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const o = value as Record<string, unknown>;
  if (typeof o.id !== "string" || !o.id.trim()) return null;
  if (typeof o.tokenName !== "string") return null;
  if (typeof o.abilityName !== "string") return null;
  if (typeof o.formula !== "string") return null;
  if (typeof o.total !== "number" || !Number.isFinite(o.total)) return null;
  if (typeof o.modifier !== "number" || !Number.isFinite(o.modifier)) return null;
  if (o.source !== "board" && o.source !== "atlas") return null;
  if (typeof o.at !== "number" || !Number.isFinite(o.at)) return null;
  if (!Array.isArray(o.faces)) return null;
  const faces: PathADiceFace[] = [];
  for (const f of o.faces.slice(0, 12)) {
    if (!f || typeof f !== "object") continue;
    const rec = f as Record<string, unknown>;
    if (typeof rec.label !== "string") continue;
    if (typeof rec.value !== "number" || !Number.isFinite(rec.value)) continue;
    if (typeof rec.sides !== "number" || !Number.isFinite(rec.sides)) continue;
    faces.push({ label: rec.label.slice(0, 24), value: rec.value, sides: rec.sides });
  }
  return {
    id: o.id.trim().slice(0, 64),
    tokenId: typeof o.tokenId === "string" ? o.tokenId.slice(0, 80) : undefined,
    tokenName: o.tokenName.trim().slice(0, 80),
    abilityName: o.abilityName.trim().slice(0, 160),
    formula: o.formula.trim().slice(0, 80),
    faces,
    modifier: o.modifier,
    total: o.total,
    higher: typeof o.higher === "string" ? o.higher.slice(0, 32) : undefined,
    extraModLabel: typeof o.extraModLabel === "string" ? o.extraModLabel.slice(0, 80) : o.extraModLabel === null ? null : undefined,
    pending: o.pending === true ? true : undefined,
    source: o.source,
    at: o.at,
  };
}

export function traitModifier(creature: Record<string, unknown>, trait: string): number {
  const key = trait.toLowerCase();
  const raw = creature[key] ?? creature[trait];
  if (typeof raw === "number" && Number.isFinite(raw)) {
    if (raw >= 6 && raw <= 30 && !key.includes("mod")) return Math.floor((raw - 10) / 2);
    return raw;
  }
  if (typeof raw === "string") {
    const m = /([+-]?\d+)/.exec(raw);
    return m ? parseInt(m[1]!, 10) : 0;
  }
  return 0;
}
